import React from 'react';
import {
  MantineReactTable,
  useMantineReactTable,
  type MRT_ColumnDef,
  MRT_ShowHideColumnsButton, MRT_ToggleFullScreenButton, MRT_ToggleGlobalFilterButton, MRT_ToggleFiltersButton,
  MRT_ToggleDensePaddingButton, MRT_TableContainer, MRT_GlobalFilterTextInput, MRT_BottomToolbar
} from 'mantine-react-table';
import {Group, ActionIcon, Tooltip, Box, Flex, Container} from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {IconBoxModel, IconBoxPadding, IconPageBreak, IconSelectAll} from "@tabler/icons-react";

// Re-export Mantine's column def type for convenience
export type { MRT_ColumnDef } from 'mantine-react-table';

export type StyledMantineTableProps<T extends object> = {
  columns: MRT_ColumnDef<T>[];
  data: T[];
  // custom actions area to render in the table's top toolbar (e.g., icon buttons)
  topToolbarActions?: React.ReactNode;
  // allow consumer to control MRT initial state (e.g., columnVisibility)
  initialState?: any;
  // optional overrides for the underlying mantine table element styles/behavior
  mantineTableProps?: {
    striped?: boolean;
    highlightOnHover?: boolean;
    withColumnBorders?: boolean;
    className?: string;
    // allow any other props that MantineReactTable forwards to Mantine <Table>
    [key: string]: any;
  };
  // optional ref target used by consumers for printing (wraps the table)
  printRef?: React.Ref<HTMLDivElement>;
  // deprecated: previously used to show an "All" option in rows-per-page selector (kept for type compatibility)
  includeAllRowsOption?: boolean;
  // whether to show a toggle button to disable pagination and show all rows
  includeShowAllToggle?: boolean;
  //IDs of columns that should only be shown at/above the md breakpoint. Below md they will be hidden automatically.
  columnsShowAtMd?: string[];
  // Optional media query override for md breakpoint. Defaults to Mantine's md ~ 48em.
  mdQuery?: string;
  // pass-through for MRT row props to enable row click handlers / styling from consumers
  mantineTableBodyRowProps?: any;
  // enable MRT bottom toolbar (which usually contains pagination)
  enableBottomToolbar?: boolean;
  // enable MRT expandable rows
  enableExpanding?: boolean;
  // render detail panel content for an expanded row
  renderDetailPanel?: (props: { row: any; table: any }) => React.ReactNode;
  // Optional visual variant for theming this table instance without affecting others
  themeVariant?: 'default' | 'audit';
};

export function StyledMantineTable<T extends object>(props: StyledMantineTableProps<T>) {
  const { columns, data, mantineTableProps, topToolbarActions, initialState, printRef, includeShowAllToggle = true, columnsShowAtMd, mdQuery, mantineTableBodyRowProps, enableBottomToolbar, enableExpanding, renderDetailPanel, themeVariant = 'default' } = props;

  // Lightweight theme tokens for the optional "audit" variant
  const auditTheme = React.useMemo(() => themeVariant === 'audit' ? {
    toolbarBg: '#0f172a', // slate-900
    toolbarFg: '#e2e8f0', // slate-200
    headBg: '#111827',    // gray-900
    headFg: '#e5e7eb',    // gray-200
    border: '#334155',    // slate-700
    rowHover: 'rgba(59,130,246,0.10)', // blue-500 @ 10%
    stripe: 'rgba(148,163,184,0.08)',  // slate-400 @ 8%
  } : null, [themeVariant]);

  // Controlled pagination so we can switch between paginated and "show all"
  const defaultPageSize = Math.max(1, initialState?.pagination?.pageSize ?? 15);
  const [pagination, setPagination] = React.useState<{ pageIndex: number; pageSize: number }>(() => ({
    pageIndex: 0,
    pageSize: defaultPageSize,
  }));
  const [showAll, setShowAll] = React.useState(false);

  // Control column visibility so we can react to breakpoint changes for specific columns
  const [columnVisibility, setColumnVisibility] = React.useState<Record<string, boolean>>({
    ...(initialState?.columnVisibility ?? {}),
  });

  // Keep pageSize synced with data length while showing all
  React.useEffect(() => {
    if (showAll) {
      setPagination(p => ({ ...p, pageSize: Math.max(1, data.length) }));
    }
  }, [showAll, data.length]);

  // Effect to toggle specified columns based on md breakpoint
  React.useEffect(() => {
    if (!columnsShowAtMd || columnsShowAtMd.length === 0) return;

    const query = mdQuery ?? '(min-width: 48em)';
    const mql = window.matchMedia(query);

    const applyVisibilityForMatch = (matches: boolean) => {
      setColumnVisibility(prev => {
        // Start from current visibility to avoid clobbering user toggles on other columns
        const next: Record<string, boolean> = { ...prev };
        for (const colId of columnsShowAtMd) {
          next[colId] = !!matches; // show at/above md, hide below
        }
        return next;
      });
    };

    // Initialize on mount
    applyVisibilityForMatch(mql.matches);

    // Listen for changes
    const listener = (e: MediaQueryListEvent) => applyVisibilityForMatch(e.matches);
    mql.addEventListener ? mql.addEventListener('change', listener) : mql.addListener(listener as any);

    return () => {
      mql.removeEventListener ? mql.removeEventListener('change', listener) : mql.removeListener(listener as any);
    };
  }, [columnsShowAtMd, mdQuery]);

  const renderAllPagination = () => includeShowAllToggle && (
    <ActionIcon
      variant="light"
      size="md"
      color="gray"
      aria-label={showAll ? 'Paginate rows' : 'Show all rows'}
      onClick={() => {
        setShowAll((s) => {
          const next = !s;
          if (next) {
            // turning on show all
            setPagination(p => ({ ...p, pageIndex: 0, pageSize: Math.max(1, data.length) }));
          } else {
            // restoring pagination to default size
            setPagination({ pageIndex: 0, pageSize: defaultPageSize });
          }
          return next;
        });
      }}

    >
      <Tooltip label={showAll? 'Paginate rows' : 'Show all rows'} position={'top-start'}>
        <IconPageBreak size={'md'}/>
      </Tooltip>
    </ActionIcon>
  )

  // Prepare merged row props so we can inject audit hover styling without clobbering consumer props
  const mergedRowPropsOption = React.useMemo(() => {
    if (!auditTheme) return mantineTableBodyRowProps;
    return (args: any) => {
      const base = typeof mantineTableBodyRowProps === 'function'
        ? (mantineTableBodyRowProps as any)(args)
        : (mantineTableBodyRowProps || {});
      const auditSx = (t: any) => ({
        '&:hover': { backgroundColor: auditTheme.rowHover },
      });
      const sx = base?.sx
        ? Array.isArray(base.sx) ? [auditSx, ...base.sx] : [auditSx, base.sx]
        : auditSx;
      return { ...base, sx };
    };
  }, [mantineTableBodyRowProps, auditTheme]);

  // Create the Mantine React Table instance via hook (v2 API)
  const table = useMantineReactTable({
    columns,
    data,
    initialState,
    state: { pagination, columnVisibility },
    onPaginationChange: setPagination,
    onColumnVisibilityChange: setColumnVisibility,
    enablePagination: !showAll,
    enableBottomToolbar: (enableBottomToolbar !== undefined) ? enableBottomToolbar : !showAll,
    // Expanding rows support (opt-in)
    ...(enableExpanding ? { enableExpanding } : {}),
    ...(renderDetailPanel ? { renderDetailPanel } : {}),
    ...(renderDetailPanel ? { getRowCanExpand: () => true } : {}),
    // allow consumers to control row props (e.g., onClick) so rows can be selectable
    ...(mergedRowPropsOption ? { mantineTableBodyRowProps: mergedRowPropsOption } : {}),
    // Make the built-in toolbar (global search, view options, etc.) take the full
    // available width and horizontally scroll on overflow instead of wrapping.
    mantineTopToolbarProps: {
      style: {
        display: 'flex',
        // Do not wrap; allow horizontal scrolling when content overflows
        flexWrap: 'nowrap',
        alignItems: 'center',
        gap: '8px',
        overflowX: 'auto',
        overflowY: 'hidden',
        WebkitOverflowScrolling: 'touch',
        whiteSpace: 'nowrap',
        alignContent: 'center',
        // Stretch to use entire toolbar row width
        justifyContent: 'space-between',
        width: '100%',
        alignSelf: 'stretch',
        flex: '1 1 auto',
        // Make the bar above the header transparent for the audit theme
        ...(auditTheme ? { backgroundColor: 'transparent' } : {}),
      },
    },
    mantineFilterTextInputProps: {
      styles: {
        input: {
          color: 'white'
        }
      }
    },
    ...(auditTheme ? {
      mantinePaperProps: {
        style: { borderColor: auditTheme.border },
      },
      mantineTableHeadCellProps: {
        style: {
          backgroundColor: auditTheme.headBg,
          color: auditTheme.headFg,
          borderColor: auditTheme.border,
        },
      },
      mantineTableBodyCellProps: {
        style: { borderColor: auditTheme.border },
      },
      mantineBottomToolbarProps: {
        style: {
          backgroundColor: 'transparent',
          color: auditTheme.toolbarFg,
          borderColor: auditTheme.border,
        }
      },
    } : {}),
    mantineTableProps: {
      striped: true,
      highlightOnHover: true,
      withColumnBorders: true,
      ...(auditTheme ? { styles: { tbody: { backgroundColor: undefined } } } : {}),
      ...(mantineTableProps || {}),
    },
  });

  return (
    <Box sx={{padding: '16px' }}>
      {/* Our Custom External Top Toolbar */}
      <Flex
        sx={(theme) => ({
          gap: '16px',
          justifyContent: 'space-between',
          width: '100%',
          // Transparent background for the external toolbar wrapper when using the audit theme
          ...(auditTheme ? { backgroundColor: 'transparent', borderRadius: '8px', padding: '6px 8px' } : {}),
        })}
      >
        <Box style={{justifyContent: 'space-between', display: 'flex', flexWrap: 'wrap', alignItems: 'center', flex: '1 1 auto'}}>
          {/* Left: custom actions area grows to take available space */}
          <Group style={{ flexWrap: 'nowrap', marginLeft: '0', flex: '1 1 auto', minWidth: 0 }}>
            {topToolbarActions}
          </Group>
          {/* Right: built-in MRT action icon buttons */}
          <Group spacing={1} style={{ flexWrap: 'nowrap', marginBottom: '0.5rem', marginLeft: 'auto' }}>
            <MRT_GlobalFilterTextInput table={table}/>
            <MRT_ToggleGlobalFilterButton table={table}/>
            <MRT_ToggleFiltersButton table={table}/>
            <MRT_ShowHideColumnsButton table={table} />
            <MRT_ToggleDensePaddingButton table={table}/>
            {renderAllPagination()}
          </Group>
        </Box>
      </Flex>
      <Container fluid ref={printRef} sx={{padding: '0px'}}>
        <MRT_TableContainer table={table}/>
        <MRT_BottomToolbar table={table} />
      </Container>
    </Box>
  );
}

export default StyledMantineTable;
